import { useMemo, useState } from "react";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import {
  Circle,
  MapContainer,
  Marker,
  Popup,
  TileLayer,
  ZoomControl,
} from "react-leaflet";
import { zones } from "../data/zones";
import { site } from "../lib/site";

const CENTER: [number, number] = [site.geo.lat, site.geo.lng];
const RINGS = [
  { km: 5, color: "#0891B2", fill: "#22D3EE", fillOpacity: 0.2, dash: "" },
  { km: 10, color: "#0891B2", fill: "#67E8F9", fillOpacity: 0.12, dash: "6 4" },
  { km: 30, color: "#0E7490", fill: "#A5F3FC", fillOpacity: 0.07, dash: "4 6" },
] as const;

const zoneIcon = L.divIcon({
  className: "anexya-pin",
  html: `
    <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style="filter:drop-shadow(0 1px 2px rgba(15,23,42,0.3))">
      <circle cx="12" cy="12" r="9.5" fill="white" stroke="#0891B2" stroke-width="2.4"/>
      <circle cx="12" cy="12" r="4" fill="#0891B2"/>
    </svg>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
  popupAnchor: [0, -10],
});

export function InteractiveMap() {
  const [scrollZoom, setScrollZoom] = useState(false);
  const markers = useMemo(() => zones.filter((z) => z.distanceKm > 0), []);

  return (
    <figure className="relative rounded-3xl bg-white border border-slate-200 shadow-soft overflow-hidden">
      <figcaption className="sr-only">
        Carte interactive du rayon d'intervention d'{site.name} centré sur Vichy :
        cercles concentriques à 5, 10 et 30 km, marqueurs des communes desservies dans l'Allier.
      </figcaption>

      <div className="flex items-center justify-between gap-4 border-b border-slate-100 px-5 py-3.5 bg-gradient-to-r from-brand-50 to-white">
        <div>
          <div className="text-xs uppercase tracking-wider text-brand-700 font-semibold">
            Champ d'action
          </div>
          <div className="text-sm text-ink-muted">
            Cercles à 5, 10 et 30 km autour de Vichy · cliquez sur un pin
          </div>
        </div>
        <div className="hidden sm:flex items-center gap-3 text-xs">
          {RINGS.map((r) => (
            <span key={r.km} className="flex items-center gap-1.5 text-ink-muted">
              <span
                className="inline-block h-2.5 w-2.5 rounded-full"
                style={{ backgroundColor: r.fill, border: `1.5px solid ${r.color}` }}
              />
              {r.km} km
            </span>
          ))}
        </div>
      </div>

      <div className="relative h-[440px] sm:h-[540px]">
        <MapContainer
          center={CENTER}
          zoom={10}
          minZoom={9}
          maxZoom={16}
          scrollWheelZoom={scrollZoom}
          zoomControl={false}
          className="h-full w-full"
          attributionControl={true}
        >
          <TileLayer
            url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
            subdomains="abcd"
            maxZoom={19}
          />

          {[...RINGS].reverse().map((r) => (
            <Circle
              key={r.km}
              center={CENTER}
              radius={r.km * 1000}
              pathOptions={{
                color: r.color,
                weight: 1.4,
                opacity: 0.8,
                fillColor: r.fill,
                fillOpacity: r.fillOpacity,
                dashArray: r.dash || undefined,
              }}
            />
          ))}

          {markers.map((z) => (
            <Marker key={z.name} position={[z.lat, z.lng]} icon={zoneIcon}>
              <Popup>
                <div className="leaflet-popup-anexya">
                  <div className="font-display font-bold text-brand-900">
                    {z.name}{" "}
                    <span className="font-normal text-ink-muted">· {z.postalCode}</span>
                  </div>
                  <div className="text-xs text-brand-700 font-semibold mt-0.5">
                    {z.distanceKm} km de Vichy
                  </div>
                  <p className="text-xs text-ink-muted mt-1.5 leading-relaxed">
                    {z.description}
                  </p>
                </div>
              </Popup>
            </Marker>
          ))}

          <ZoomControl position="bottomright" />
        </MapContainer>

        {!scrollZoom && (
          <button
            type="button"
            onClick={() => setScrollZoom(true)}
            className="absolute bottom-4 left-4 z-[500] inline-flex items-center min-h-[44px] rounded-full bg-white/95 backdrop-blur px-4 py-2.5 text-xs font-semibold text-brand-800 shadow-soft border border-brand-100 hover:bg-white transition-colors cursor-pointer focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
            aria-label="Activer le zoom à la molette de la souris"
          >
            Cliquez pour activer le zoom molette
          </button>
        )}
      </div>

      <div className="border-t border-slate-100 px-5 py-3 text-xs text-ink-muted flex flex-wrap items-center gap-x-5 gap-y-1">
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block h-2 w-2 rounded-full border-2 border-brand-600 bg-white" />
          Commune desservie ({markers.length})
        </span>
        <span className="ml-auto text-ink-soft">Carte © OpenStreetMap / CARTO</span>
      </div>
    </figure>
  );
}
